import * as React from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '@/ui'
import { faArrowRotateLeftSolid, faClockRotateLeft } from '@/lib/fa'
import { ConfigurationType } from '@/app/constants/configuration/configurationType'
import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { usePreviousConfiguration } from '../hooks/usePreviousConfiguration'
import { useRestoreDefaultSettingsMutation } from '../hooks/useRestoreDefaultSettingsMutation'

export type ConfigurationRestoreOptions = {
  configurationType: ConfigurationType
  // Klucz tłumaczenia nazwy zakładki, np. 'configuration-menu.violations'.
  tabLabelKey: string
  queryKeys: string[]
  serverData: unknown
  // Zakładki z własnym przywracaniem domyślnych (płaca minimalna, stawki delegacji) dają false.
  withDefaults?: boolean
  // Dla zakładek, których dane nie odświeżą się same po invalidacji queryKeys.
  onChanged?: () => void
}

type Props = {
  options: ConfigurationRestoreOptions
  isDirty: boolean
}

export const ConfigurationRestoreActions = ({ options, isDirty }: Props) => {
  const { companyId = '' } = useParams<{ companyId: string }>()
  const { t } = useLocalize()
  const wrapConfirm = useWrapConfirm()
  const previous = usePreviousConfiguration(companyId, options)
  const restoreDefaults = useRestoreDefaultSettingsMutation(companyId, options)

  return (
    <>
      {options.withDefaults && (
        <Button
          disabled={isDirty}
          loading={restoreDefaults.isPending}
          className="tw-rounded-lg tw-px-4"
          variant="secondary"
          size="sm"
          faLeadingIcon={faArrowRotateLeftSolid}
          data-test-id="settings-restore-defaults"
          text={t('configuration-menu.restore').toString()}
          onClick={wrapConfirm(() => restoreDefaults.mutate(undefined, { onSuccess: options.onChanged }), {
            message: restoreDefaults.confirmMessage,
          })}
        />
      )}
      <Button
        disabled={isDirty || !previous.isAvailable}
        loading={previous.revert.isPending}
        className="tw-rounded-lg tw-px-4"
        variant="secondary"
        size="sm"
        faLeadingIcon={faClockRotateLeft}
        data-test-id="settings-revert-last-save"
        text={t('configuration.revert').toString()}
        title={
          isDirty
            ? t('configuration.revert-unsaved-hint').toString()
            : undefined
        }
        onClick={wrapConfirm(() => previous.revert.mutate(undefined, { onSuccess: options.onChanged }), {
          message: previous.confirmMessage,
        })}
      />
    </>
  )
}
